/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDoSeeDataContext } from "../../../context/doSeeData/DoSeeDataContext";
import { pushOneThingsToDo } from "../../../middlewares/sysNavigation/SysNavigation";
import { BreadCrumbComponent } from "../../../middlewares/breadCrumb/BreadCrumbComponent";
import {
  Modal,
  GoBack,
  MyButton,
  MySearch,
  Pagination,
  DashboardIntro,
} from "../../home/component/Component";
import { _TYPES_ } from "../utils/types";
import { _PAGE_SIZE_ } from "../../../middlewares/other/tools";
import { useGeoPositionContext } from "../utils/geoPosition";
import { useAdminContext } from "../../../context/admin/context/AdminContext";
import PromoteContent from "../utils/PromoteContent";
import { FaRegTrashAlt } from "react-icons/fa";
import { RiRefreshLine } from "react-icons/ri";

const addRoute = "/data/things/todo/add";
const editRoute = "/data/things/todo/edit";
const doseeDataRoutes = "/dosee/data";

const Overview = () => {
  const [thingsToDosList, setThingsToDosList] = useState([]);
  const { thingsToDos, thingsToDosCount, fetchDoSeeData, deleteOneContent } =
    useDoSeeDataContext();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(_PAGE_SIZE_);
  const [localSearch, setLocalSearch] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedContentIds, setSelectedContentIds] = useState([]);

  const {
    country_id,
    entry_label,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  // design tokens
  const container = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
  const card = "rounded-2xl border border-slate-200 bg-white shadow-sm";
  const sectionPad = "p-4 sm:p-5";
  const btnBase =
    "inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed";
  const btnPrimary =
    "bg-blue-600 text-white border-blue-600 hover:bg-blue-500 focus-visible:ring-blue-500";
  const btnNeutral =
    "bg-white text-slate-700 border-slate-200 hover:bg-slate-50";
  const btnDanger =
    "bg-red-600 text-white border-red-600 hover:bg-red-500 focus-visible:ring-red-500";

  const data_request_body = {
    country_id,
    first_level_id,
    second_level_id,
    third_level_id,
    fourth_level_id,
    content: _TYPES_.THINGSTODO,
  };

  useEffect(() => {
    if (!thingsToDos) fetchDoSeeData(data_request_body, page, pageSize);
    setThingsToDosList(thingsToDos || []);
  }, [thingsToDos]);

  const reloadData = () => fetchDoSeeData(data_request_body, page, pageSize);

  const SearchList = (e) => {
    const key = e.target.value.toString().toLowerCase();
    if (!thingsToDos || !Array.isArray(thingsToDos)) return;
    if (!key) {
      setThingsToDosList(thingsToDos);
      return;
    }
    const searchArray = thingsToDos.filter((ttd) =>
      ttd?.title?.toString().toLowerCase().includes(key)
    );
    setThingsToDosList(searchArray);
  };

  // --- Selection logic (robust & page-aware) ---
  const LocalData = localSearch?.length > 0 ? localSearch : thingsToDosList;
  const pageIds = (LocalData || [])
    .map((x) => x?.things_to_do_id)
    .filter(Boolean);
  const allSelectedOnPage =
    pageIds.length > 0 &&
    pageIds.every((id) => selectedContentIds.includes(id));
  const someSelectedOnPage =
    !allSelectedOnPage &&
    pageIds.some((id) => selectedContentIds.includes(id));

  const headerCheckboxRef = useRef(null);
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelectedOnPage;
    }
  }, [someSelectedOnPage, allSelectedOnPage, LocalData, selectedContentIds]);

  const toggleSelectAllOnPage = () => {
    setSelectedContentIds((prev) => {
      if (allSelectedOnPage) {
        // deselect only the items on the current page
        return prev.filter((id) => !pageIds.includes(id));
      }
      // add current page items (union)
      const set = new Set(prev);
      pageIds.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };

  const toggleOne = (id) => {
    setSelectedContentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const deleteMany = async () => {
    if (selectedContentIds.length === 0) return;
    setLoading(true);
    try {
      for (let i = 0; i < selectedContentIds.length; i++) {
        await deleteOneContent({
          data_id: selectedContentIds[i],
          content: _TYPES_.THINGSTODO,
        });
      }
      setSelectedContentIds([]);
      reloadData();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className={`${container} py-4 lg:py-6`}>
        <div className="mb-3">
          <BreadCrumbComponent />
        </div>

        <div className="flex items-center justify-between mb-2">
          <GoBack props={{ route: doseeDataRoutes }} />
        </div>

        <div className="mb-4">
          <DashboardIntro props={{ intro: `Things To Do`, name: entry_label }} />
        </div>

        <div className="space-y-4">
          {/* Search + Add */}
          <div className={`${card} ${sectionPad}`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <MySearch
                type={_TYPES_.THINGSTODO}
                value={localSearch}
                showGlobalSearch={true}
                setValue={setLocalSearch}
                onChange={SearchList}
              />
              {country_id ? (
                <Link to={addRoute} className="shrink-0">
                  <MyButton
                    title="Add Things To Do"
                    className={`${btnBase} ${btnPrimary}`}
                    onClick={() => { }}
                  />
                </Link>
              ) : null}
            </div>
          </div>

          {/* Bulk actions */}
          <div className={`${card} ${sectionPad}`}>
            <div className="flex flex-wrap items-center gap-3">
              <label className={`${btnBase} ${btnNeutral} cursor-pointer`}>
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  checked={allSelectedOnPage}
                  onChange={toggleSelectAllOnPage}
                  className="h-4 w-4 rounded border-slate-300"
                  aria-label={
                    allSelectedOnPage ? "Unselect all on page" : "Select all on page"
                  }
                />
                <span className="text-slate-700">
                  {allSelectedOnPage
                    ? "Unselect All (This Page)"
                    : someSelectedOnPage
                      ? "Select All (This Page)"
                      : "Select All (This Page)"}
                </span>
              </label>

              <MyButton
                onClick={deleteMany}
                className={`${btnBase} ${selectedContentIds?.length > 0 ? btnDanger : btnNeutral
                  }`}
                disabled={selectedContentIds?.length === 0 || loading}
              >
                {loading ? (
                  <RiRefreshLine className="text-lg animate-spin" />
                ) : (
                  <FaRegTrashAlt className="text-base" />
                )}
                <span>
                  Delete{" "}
                  {selectedContentIds?.length > 0 ? selectedContentIds.length : ""}{" "}
                  {selectedContentIds?.length === 1 ? "Item" : "Items"}
                </span>
              </MyButton>
            </div>

            {/* Table */}
            <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold w-[140px]">
                      Select
                    </th>
                    <th className="px-4 py-3 text-left font-semibold">Title</th>
                    <th className="px-4 py-3 text-left font-semibold">Slug</th>
                    <th className="px-4 py-3 text-center font-semibold w-[220px]">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {(LocalData || []).length > 0 ? (
                    LocalData.map((ttd) => {
                      const id = ttd?.things_to_do_id;
                      const isSelected = selectedContentIds.includes(id);
                      return (
                        <tr
                          key={id}
                          className="hover:bg-slate-50 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <label className="inline-flex items-center gap-2">
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded border-slate-300"
                                checked={!!isSelected}
                                onChange={() => toggleOne(id)}
                                aria-label={`Select ${ttd?.title ?? "row"}`}
                              />
                              <span className="text-xs text-slate-500">
                                {isSelected ? "Selected" : "Select"}
                              </span>
                            </label>
                          </td>
                          <td className="px-4 py-3">{ttd?.title}</td>
                          <td className="px-4 py-3">{ttd?.slug}</td>
                          <td className="px-2 py-3">
                            <ActionButton props={ttd} reloadData={reloadData} />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr className="animate-pulse">
                      <td className="px-4 py-3">...</td>
                      <td className="px-4 py-3">...</td>
                      <td className="px-4 py-3">...</td>
                      <td className="px-4 py-3">...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className={`${card} ${sectionPad}`}>
            <Pagination
              page={page}
              setPage={(p) => {
                setPage(p);
                // optional: clear header checkbox indeterminate on page change
                if (headerCheckboxRef.current)
                  headerCheckboxRef.current.indeterminate = false;
              }}
              pageSize={pageSize}
              setPageSize={setPageSize}
              total_items={thingsToDosCount}
              callbackFunction={(p, size) => {
                fetchDoSeeData(data_request_body, p, size);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * @author Ntavigwa Bashombe
 */
const ActionButton = ({ props, reloadData }) => {
  const { sysAdmin } = useAdminContext();
  const isSuperAdmin = ["super_admin", "admin"].includes(sysAdmin?.role);
  const { thingsToDos, deleteOneContent, updateOneContent } =
    useDoSeeDataContext();

  const [showModal, setShowModal] = useState(false);
  const [promote, setPromote] = useState(false);
  const [promoteLoader, setPromoteLoader] = useState(false);

  const things_to_do_id = props.things_to_do_id;
  const thingstodo_title = props.title;
  const thingstodo = props;

  const deleteOneThingsToDoById = async (id) => {
    const { status } = await deleteOneContent({
      data_id: id,
      content: _TYPES_.THINGSTODO,
    });
    if (status === 200) {
      reloadData();
    }
  };

  const buildData = (object, position) => ({
    promoted: `${position}`,
    country_id: object?.country_id,
    description: object?.description,
    first_level_id: object?.first_level_id,
    fourth_level_id: object?.fourth_level_id,
    second_level_id: object?.second_level_id,
    slug: object?.slug,
    status: object?.status,
    third_level_id: object?.third_level_id,
    thumbnail: object?.thumbnail,
    title: object?.title,
  });

  const promotion = async (position) => {
    const previousPromoted = thingsToDos?.find(
      (t) => t?.promoted === String(position)
    );
    setPromoteLoader(true);

    const { status } = await updateOneContent({
      data_id: thingstodo?.things_to_do_id,
      data: buildData(props, position),
      content: _TYPES_.THINGSTODO,
    });

    if (previousPromoted) {
      await updateOneContent({
        data_id: previousPromoted?.things_to_do_id,
        data: buildData(previousPromoted, "no"),
        content: _TYPES_.THINGSTODO,
      });
    }

    if (status === 200) {
      reloadData();
    }
    setPromoteLoader(false);
  };

  const suspension = async (row) => {
    const data = {
      country_id: row?.country_id,
      description: row?.description,
      first_level_id: row?.first_level_id,
      fourth_level_id: row?.fourth_level_id,
      second_level_id: row?.second_level_id,
      slug: row?.slug,
      status: row?.status === 1 ? 0 : 1,
      third_level_id: row?.third_level_id,
      thumbnail: row?.thumbnail,
      title: row?.title,
    };

    const { status } = await updateOneContent({
      data_id: row?.things_to_do_id,
      data,
      content: _TYPES_.THINGSTODO,
    });

    if (status === 200) {
      reloadData();
    }
  };

  // button styles
  const btnBase =
    "inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 text-xs";
  const btnNeutral =
    "bg-white text-slate-700 border-slate-200 hover:bg-slate-50";
  const btnInfo =
    "bg-blue-700 text-white border-blue-700 hover:bg-blue-600 focus-visible:ring-blue-600";
  const btnWarn =
    "bg-amber-600 text-white border-amber-600 hover:bg-amber-500 focus-visible:ring-amber-500";
  const btnDanger =
    "bg-red-600 text-white border-red-600 hover:bg-red-500 focus-visible:ring-red-500";
  const btnPrimary =
    "bg-blue-600 text-white border-blue-600 hover:bg-blue-500 focus-visible:ring-blue-500";

  return (
    <>
      <Modal
        props={{
          name: thingstodo_title,
          showModal: showModal,
          setShowModal: () => setShowModal(false),
          delete: () => deleteOneThingsToDoById(things_to_do_id),
        }}
      />

      <PromoteContent
        visible={promote}
        setVisible={setPromote}
        loading={promoteLoader}
        ids={thingsToDos
          ?.filter(
            (t) => !["yes", "no", null, undefined].includes(t?.promoted)
          )
          .map((t) => ({
            id: t?.things_to_do_id,
            title: t?.title,
            position: parseInt(t?.promoted),
          }))}
        onClick={(position) => promotion(position)}
      />

      <div className="flex flex-row justify-end items-center px-2 py-2 gap-2">
        {isSuperAdmin ? (
          <>
            <MyButton
              title={thingstodo?.status === 1 ? "Suspend" : "Unsuspend"}
              className={`${btnBase} ${thingstodo?.status === 1 ? btnPrimary : btnWarn
                }`}
              onClick={() => suspension(thingstodo)}
            />
            <MyButton
              title={
                ["yes", "no", null, undefined].includes(thingstodo?.promoted)
                  ? "Promote"
                  : "Unpromote"
              }
              className={`${btnBase} ${["yes", "no", null, undefined].includes(thingstodo?.promoted)
                ? btnNeutral
                : btnWarn
                }`}
              onClick={() => setPromote(true)}
            />
          </>
        ) : null}

        <Link to={editRoute}>
          <MyButton
            title="Edit"
            className={`${btnBase} ${btnInfo}`}
            onClick={() => pushOneThingsToDo(thingstodo)}
          />
        </Link>
        <MyButton
          title="Delete"
          className={`${btnBase} ${btnDanger}`}
          onClick={() => setShowModal(true)}
        />
      </div>
    </>
  );
};

export default Overview;
